import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { Question } from "@tsTypes/question-schema";
import {
  ActiveTestSessionSchema,
  ActiveTestSessionsSchema,
  CreateActiveTestSessionSchema,
  UpdateTestQuestionStateSchema,
  UpdateTestSessionSchema,
  UpdateTimerStateSchema,
} from "@tsTypes/redux-schema";
import { roundTo2DP } from "@utils/helpers";
import { useMemo } from "react";
import { RootState } from "../../app/store";
import { useCustomDispatch, useCustomSelector } from "../../lib/redux/hooks";

const initialState: ActiveTestSessionsSchema = {};

export const activeTestSessionsSlice = createSlice({
  name: "activeTestSessions",
  initialState,
  reducers: {
    createActiveTestSession: (
      state,
      action: PayloadAction<CreateActiveTestSessionSchema>
    ) => {
      const { testId } = action.payload;

      const newSession: ActiveTestSessionSchema = {
        currentQuestion: 0,
        testStatus: "paused",
        questionStates: {},
        currentSeconds: 0,
      };

      state[testId] = newSession;
    },

    updateTestSession: (
      state,
      action: PayloadAction<UpdateTestSessionSchema>
    ) => {
      const { testId, updates } = action.payload;

      if (state[testId]) {
        state[testId] = {
          ...state[testId],
          ...updates,
        };
      }
    },

    updateTestQuestionState: (
      state,
      action: PayloadAction<UpdateTestQuestionStateSchema>
    ) => {
      const { testId, questionId, questionState } = action.payload;
      if (state[testId]) {
        if (!state[testId]!.questionStates) {
          state[testId]!.questionStates = {};
        }
        // Merge with existing state instead of replacing
        const currentState = state[testId]!.questionStates[questionId] || {};

        // Deep merge for nested objects like textAnswers
        const mergedState = { ...currentState };
        Object.keys(questionState).forEach((key) => {
          if (
            key === "textAnswers" &&
            currentState.textAnswers &&
            questionState.textAnswers
          ) {
            mergedState.textAnswers = {
              ...currentState.textAnswers,
              ...questionState.textAnswers,
            };
          } else {
            // @ts-ignore
            mergedState[key] = questionState[key];
          }
        });

        state[testId]!.questionStates[questionId] = mergedState;
      }
    },

    updateTimerState: (
      state,
      action: PayloadAction<UpdateTimerStateSchema>
    ) => {
      const { testId, currentSeconds } = action.payload;

      if (state[testId]) {
        state[testId]!.currentSeconds = currentSeconds;
      }
    },

    deleteActiveTestSession: (
      state,
      action: PayloadAction<{ testId: string }>
    ) => {
      const { testId } = action.payload;
      delete state[testId];
    },

    deleteAllActiveTestSessions: (state) => {
      Object.keys(state).forEach((testId) => {
        delete state[testId];
      });
    },
  },
});

export const {
  createActiveTestSession,
  updateTestSession,
  updateTestQuestionState,
  updateTimerState,
  deleteActiveTestSession,
  deleteAllActiveTestSessions,
} = activeTestSessionsSlice.actions;

// Selectors
export const selectActiveTestSession = (state: RootState, testId: string) =>
  state.activeTestSessions[testId];

export const selectTestQuestionState = (
  state: RootState,
  testId: string,
  questionId: string
) => state.activeTestSessions[testId]?.questionStates?.[questionId];

// Derived data selectors
export const selectAttemptedQuestions = (state: RootState, testId: string) => {
  const questionStates = state.activeTestSessions[testId]?.questionStates ?? {};
  return Object.entries(questionStates)
    .filter(
      ([_, state]) =>
        state.selectedOption ||
        (state.textAnswers &&
          Object.values(state.textAnswers).some(
            (answer) => answer.trim() !== ""
          ))
    )
    .map(([questionId, _]) => questionId);
};

export const selectFlaggedQuestions = (state: RootState, testId: string) => {
  const questionStates = state.activeTestSessions[testId]?.questionStates ?? {};
  return Object.entries(questionStates)
    .filter(([_, state]) => state.isFlagged)
    .map(([questionId, _]) => questionId);
};

export const selectTestScores = (
  state: RootState,
  testId: string,
  testQuestions: Question[]
) => {
  return Object.entries(
    state.activeTestSessions[testId]?.questionStates ?? {}
  ).reduce((acc, [questionId, questionState]) => {
    const relevantQuestionMarks = testQuestions.find(
      (q) => q.documentId === questionId
    )?.TotalMarks;
    if (!questionState.marks || !relevantQuestionMarks) {
      return acc;
    }
    const percentagePerformance = roundTo2DP(
      (questionState.marks / relevantQuestionMarks) * 100
    );
    acc[questionId] = percentagePerformance;
    return acc;
  }, {} as { [questionId: string]: number | undefined });
};

export const selectTestMarks = (state: RootState, testId: string) => {
  const questionStates = state.activeTestSessions[testId]?.questionStates ?? {};
  return Object.entries(questionStates).reduce(
    (acc, [questionId, questionState]) => {
      acc[questionId] = questionState.marks;
      return acc;
    },
    {} as { [questionId: string]: number | undefined }
  );
};

export const selectTestAllQuestionStates = (
  state: RootState,
  testId: string
) => {
  return state.activeTestSessions[testId]?.questionStates ?? {};
};

// Custom hook for automatic session initialization with derived state
export const useActiveTestSession = (testId: string) => {
  const dispatch = useCustomDispatch();
  const activeTestSession = useCustomSelector((state) =>
    selectActiveTestSession(state, testId)
  );

  // Auto-initialize session if it doesn't exist
  if (!activeTestSession) {
    dispatch(
      createActiveTestSession({
        testId,
      })
    );
  }

  // Get derived data - memoized selectors created once per testId
  const attemptedQuestionsSelector = useMemo(
    () =>
      createSelector(
        [
          (state: RootState) =>
            state.activeTestSessions[testId]?.questionStates ?? {},
        ],
        (questionStates) => {
          return Object.entries(questionStates)
            .filter(
              ([_, state]) =>
                state.selectedOption ||
                (state.textAnswers &&
                  Object.values(state.textAnswers).some(
                    (answer) => answer.trim() !== ""
                  ))
            )
            .map(([questionId, _]) => questionId);
        }
      ),
    [testId]
  );
  const flaggedQuestionsSelector = useMemo(
    () =>
      createSelector(
        [
          (state: RootState) =>
            state.activeTestSessions[testId]?.questionStates ?? {},
        ],
        (questionStates) => {
          return Object.entries(questionStates)
            .filter(([_, state]) => state.isFlagged)
            .map(([questionId, _]) => questionId);
        }
      ),
    [testId]
  );
  const testMarksSelector = useMemo(
    () =>
      createSelector(
        [
          (state: RootState) =>
            state.activeTestSessions[testId]?.questionStates ?? {},
        ],
        (questionStates) => {
          return Object.entries(questionStates).reduce(
            (acc, [questionId, questionState]) => {
              acc[questionId] = questionState.marks;
              return acc;
            },
            {} as { [questionId: string]: number | undefined }
          );
        }
      ),
    [testId]
  );

  const attemptedQuestions = useCustomSelector(attemptedQuestionsSelector);
  const flaggedQuestions = useCustomSelector(flaggedQuestionsSelector);
  const testMarks = useCustomSelector(testMarksSelector);
  // const testSubtopics =

  // Helper functions
  const setCurrentQuestion = (
    questionIndex: number | ((prev: number) => number)
  ) => {
    const currentQuestion = activeTestSession?.currentQuestion ?? 0;
    const newIndex =
      typeof questionIndex === "function"
        ? questionIndex(currentQuestion)
        : questionIndex;
    dispatch(
      updateTestSession({
        testId,
        updates: { currentQuestion: newIndex },
      })
    );
  };

  const setTestStatus = (
    status:
      | "paused"
      | "taking"
      | "marking"
      | "reviewing"
      | ((
          prev: "paused" | "taking" | "marking" | "reviewing"
        ) => "paused" | "taking" | "marking" | "reviewing")
  ) => {
    const testStatus = activeTestSession?.testStatus ?? "paused";
    const newStatus =
      typeof status === "function" ? status(testStatus) : status;
    dispatch(
      updateTestSession({
        testId,
        updates: { testStatus: newStatus },
      })
    );
  };

  const updateTimer = (timerUpdates: { currentSeconds?: number }) => {
    const currentSeconds = activeTestSession?.currentSeconds ?? 0;
    const newSeconds = timerUpdates.currentSeconds ?? currentSeconds;
    dispatch(
      updateTimerState({
        testId,
        currentSeconds: newSeconds,
      })
    );
  };

  return {
    // Session data
    showOverview:
      activeTestSession?.testStatus === "paused" &&
      attemptedQuestions.length === 0 &&
      activeTestSession?.currentSeconds === 0,
    activeTestSession,
    currentQuestion: activeTestSession?.currentQuestion ?? 0,
    testStatus: activeTestSession?.testStatus ?? "paused",
    currentSeconds: activeTestSession?.currentSeconds ?? 0,

    // Derived data
    attemptedQuestions,
    flaggedQuestions,
    testMarks,

    // Actions
    setCurrentQuestion,
    setTestStatus,
    updateTimer,
  };
};

export default activeTestSessionsSlice.reducer;
